import { useMemo, useState } from 'react';
import { useSelected } from '../../providers/selectedContext';
import { useTheme } from '../../providers/theme';
import { getColors } from '../../components/utils/getColors';
import '../../components/dashboard/components/tables.css';
import { ConfigProvider, Descriptions, Radio, RadioChangeEvent } from 'antd';
import { Accordion } from '../accordion';
import {
  BROOKS_COREY_EXPONENT_LABEL,
  BROOKS_COREY_PORE_SIZE_INDEX_LABEL,
  roundBrooksCoreyDisplayValue,
  roundBrooksCoreyPoreSizeDistributionIndex,
} from '../../utils/brooksCorey';

const SampleResults = () => {
  const { selectedInfo } = useSelected();
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const [showLambda, setShowLambda] = useState(false);

  const liaoData = useMemo(() => {
    if (!selectedInfo?.sample?.analysis_values?.liao) return null;
    return {
      Pce: selectedInfo.sample.analysis_values.liao.pce,
      A: selectedInfo.sample.analysis_values.liao.best_a?.toFixed(4),
      B: selectedInfo.sample.analysis_values.liao.best_b?.toFixed(4),
      Lambda: selectedInfo.sample.analysis_values.liao.lambda_variable?.toFixed(4),
      Pcmax: selectedInfo.sample.analysis_values.liao.true_pc_max,
      Swir: selectedInfo.sample.analysis_values.liao.swir?.toFixed(4),
    };
  }, [selectedInfo?.sample?.analysis_values?.liao]);

  const lambdaData = useMemo(() => {
    const values = selectedInfo?.sample?.analysis_values.lambda_values;
    if (!values) return null;
    return {
      'Ln (PC) Sum': roundBrooksCoreyDisplayValue(values.ln_pc_sum),
      'Ln (PC)² Sum': roundBrooksCoreyDisplayValue(values.ln_pc_squared_sum),
      'Ln (Swn) Sum': roundBrooksCoreyDisplayValue(values.ln_swn_sum),
      'Ln (Swn)² Sum': roundBrooksCoreyDisplayValue(values.ln_swn_squared_sum),
      Pe: roundBrooksCoreyDisplayValue(values.pe),
      [BROOKS_COREY_EXPONENT_LABEL]: roundBrooksCoreyDisplayValue(
        values.lambda_value
      ),
      [BROOKS_COREY_PORE_SIZE_INDEX_LABEL]:
        roundBrooksCoreyPoreSizeDistributionIndex(values.lambda_value),
    };
  }, [selectedInfo?.sample?.analysis_values?.lambda_values]);

  const tableData = useMemo(() => (showLambda ? lambdaData : liaoData), [showLambda, lambdaData, liaoData]);

  if (!liaoData && !lambdaData) {
    return (
      <Accordion defaultOpen="sample-results">
        <Accordion.Item id="sample-results" header="Sample Results">
          <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.7, color: colors.text }}>
            No analysis data available. Go to <strong>Sample Measurements</strong> to add Sw and Pc values and click Calculate.
          </p>
        </Accordion.Item>
      </Accordion>
    );
  }

  const handleDataTypeChange = (event: RadioChangeEvent) => setShowLambda(event.target.value);

  return (
    <Accordion defaultOpen="sample-results">
      <Accordion.Item id="sample-results" header="Sample Results">
        <Radio.Group
          onChange={handleDataTypeChange}
          options={[
            { label: 'Liao Analysis', value: false },
            { label: 'Brooks-Corey Analysis', value: true },
          ]}
          defaultValue={false}
          style={{ width: '100%', backgroundColor: 'transparent !important', paddingBottom: '1rem' }}
          block
          optionType="button"
          buttonStyle="solid"
          className={isDay ? 'day-radio' : 'night-radio'}
        />
        <ConfigProvider
          theme={{
            components: {
              Descriptions: {
                colorBgBase: colors.bg,
                contentColor: colors.text,
                labelBg: isDay ? '#fafafa' : '#090909',
                colorBorder: colors.border,
                extraColor: colors.border,
              },
            },
            token: {
              colorText: colors.text,
              colorBorder: colors.border,
              colorBgBase: colors.bg,
              colorPrimaryHover: colors.colorPrimary,
              colorPrimary: colors.colorPrimary,
            },
          }}
        >
          <Descriptions bordered column={1} className="samples-descriptions">
            {tableData ? (
              Object.entries(tableData).map(([key, value]) => (
                <Descriptions.Item key={key} label={<span style={{ color: colors.text }}>{key.replace('_', ' ')}</span>}>
                  {value ?? 'N/A'}
                </Descriptions.Item>
              ))
            ) : (
              <div className="no-data-div" style={{ marginTop: '1rem' }}>
                No results data available.
              </div>
            )}
          </Descriptions>
        </ConfigProvider>
      </Accordion.Item>
    </Accordion>
  );
};

export default SampleResults;
